import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { inr } from "@/lib/format";
import { cn } from "@/lib/utils";

const key = (d) => d.toISOString().slice(0, 10);
export const fmt = (k) => new Date(`${k}T00:00:00Z`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", timeZone: "UTC" });

export const weekRange = (offset) => {
  const n = new Date();
  const start = new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate()));
  start.setUTCDate(start.getUTCDate() - ((start.getUTCDay() + 6) % 7) - offset * 7);
  const end = new Date(start); end.setUTCDate(end.getUTCDate() + 6);
  return [key(start), key(end)];
};

const summarize = (daily, [fk, tk]) => {
  const rows = daily.filter((d) => d.date_key >= fk && d.date_key <= tk);
  const s = rows.reduce((a, d) => ({
    orders: a.orders + (d.orders || 0), multi: a.multi + (d.multi_orders || 0),
    multiEarn: a.multiEarn + (d.multi_earnings || 0), earn: a.earn + (d.total_earnings || 0),
  }), { orders: 0, multi: 0, multiEarn: 0, earn: 0 });
  const best = rows.reduce((b, d) => (!b || d.total_earnings > b.total_earnings ? d : b), null);
  return { ...s, normal: s.orders - s.multi, days: rows.length, best };
};

export const waUrl = (mobile, text) => {
  const d = (mobile || "").replace(/\D/g, "");
  return `https://wa.me/${d.length === 10 ? `91${d}` : d}?text=${encodeURIComponent(text)}`;
};

export const message = (dp, s, [fk, tk]) =>
  `Hi ${dp.full_name}, here is your Connect Tym weekly summary (${fmt(fk)} – ${fmt(tk)}):\n\n` +
  `FE ID: ${dp.fe_id}\nActive days: ${s.days}\nTotal orders: ${s.orders}\n• Normal orders: ${s.normal}\n• Multi orders: ${s.multi}${s.multiEarn ? ` (${inr(s.multiEarn)})` : ""}\n` +
  `Total earnings: ${inr(s.earn)}${s.best ? `\nBest day: ${fmt(s.best.date_key)} – ${inr(s.best.total_earnings)}` : ""}\n\nThank you for delivering with Connect Tym!`;

export function WeeklySummaryDialog({ dp, daily, open, onClose }) {
  const [offset, setOffset] = useState(0);
  const range = useMemo(() => weekRange(offset), [offset]);
  const s = useMemo(() => summarize(daily || [], range), [daily, range]);
  const text = dp ? message(dp, s, range) : "";
  const wa = waUrl(dp?.mobile, text);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success("Summary copied"); } catch (e) { toast.error("Could not copy"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card sm:max-w-md" data-testid="weekly-summary-dialog">
        <DialogHeader><DialogTitle className="font-head">Weekly Earnings Summary</DialogTitle></DialogHeader>
        <div className="flex rounded-lg border border-border p-1 text-sm">
          {["This Week", "Last Week"].map((l, i) => (
            <button key={l} onClick={() => setOffset(i)} data-testid={`weekly-${i ? "last" : "this"}`}
              className={cn("flex-1 rounded-md py-1.5 transition-colors", offset === i ? "bg-primary text-primary-foreground" : "hover:bg-accent")}>{l}</button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground" data-testid="weekly-range">{fmt(range[0])} – {fmt(range[1])} · Mon to Sun</p>
        <div className="grid grid-cols-3 gap-2" data-testid="weekly-stats">
          <Stat l="Total Orders" v={s.orders} id="orders" />
          <Stat l="Normal" v={s.normal} id="normal" />
          <Stat l="Multi" v={s.multi} id="multi" />
          <Stat l="Active Days" v={s.days} id="days" />
          <Stat l="Multi Earnings" v={inr(s.multiEarn)} id="multi-earn" />
          <Stat l="Total Earnings" v={inr(s.earn)} id="earnings" strong />
        </div>
        <pre className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-lg bg-secondary/50 p-3 text-xs" data-testid="weekly-message">{text}</pre>
        <div className="grid grid-cols-2 gap-2">
          <Button asChild className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="weekly-whatsapp">
            <a href={wa} target="_blank" rel="noreferrer"><MessageCircle className="mr-1 h-4 w-4" /> Send on WhatsApp</a>
          </Button>
          <Button variant="outline" onClick={copy} data-testid="weekly-copy"><Copy className="mr-1 h-4 w-4" /> Copy</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ l, v, id, strong }) => (
  <div className="rounded-lg border border-border p-2" data-testid={`weekly-stat-${id}`}>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</p>
    <p className={cn("tabular font-semibold", strong && "text-emerald-500")}>{v}</p>
  </div>
);
